import React, { useState, useEffect, useRef } from 'react';
import { Grid, CircularProgress, Box } from '@mui/material';
import { useApiGet } from '../../../utils/httpClientTs';
import {
  getIRSFormQuestions,
  updateIRSFormAnswer,
} from '../../../endpoints/irsForms';
import { IRSQuestion } from '../../../types/irsForms';
import IRSFormQuestion from './IRSFormQuestion';

// Simple debounce function
const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

interface IRSFormSectionProps {
  formId: string;
  sectionId: string;
  editable: boolean;
  formType: 'lc' | 'sc' | 'rg';
}

const IRSFormSection: React.FC<IRSFormSectionProps> = ({
  formId,
  sectionId,
  editable,
  formType,
}) => {
  const [questions, setQuestions] = useState<IRSQuestion[]>([]);
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [saving, setSaving] = useState<Record<string, boolean>>({});

  const {
    data: questionsData,
    isLoading,
    refetch,
  } = useApiGet(
    ['irs-form-questions', formId, sectionId],
    () => getIRSFormQuestions(formId, sectionId),
    {
      enabled: !!formId && !!sectionId,
      retry: 1,
    }
  );

  useEffect(() => {
    if (questionsData) {
      setQuestions(questionsData);
      // Initialize answers from questions data
      const initialAnswers: Record<string, any> = {};
      questionsData.forEach((q) => {
        if (q.decimal_answer !== null && q.decimal_answer !== undefined) {
          initialAnswers[q.question_id] = q.decimal_answer;
        } else if (q.text_answer !== null && q.text_answer !== undefined) {
          initialAnswers[q.question_id] = q.text_answer;
        } else if (
          q.boolean_answer !== null &&
          q.boolean_answer !== undefined
        ) {
          initialAnswers[q.question_id] = q.boolean_answer;
        } else if (q.date_answer) {
          initialAnswers[q.question_id] = q.date_answer;
        } else {
          initialAnswers[q.question_id] = null;
        }
      });
      setAnswers(initialAnswers);
    }
  }, [questionsData]);

  // Debounced save function
  const debouncedSaveRef = useRef<ReturnType<typeof debounce> | null>(null);

  useEffect(() => {
    debouncedSaveRef.current = debounce(
      async (questionId: string, value: any, answerType: string) => {
        setSaving((prev) => ({ ...prev, [questionId]: true }));
        try {
          const updateData: any = {};
          if (answerType === 'decimal') {
            updateData.decimal_answer =
              value !== null && value !== '' ? parseFloat(String(value)) : null;
          } else if (answerType === 'text') {
            updateData.text_answer = value || null;
          } else if (answerType === 'boolean') {
            updateData.boolean_answer = value;
          } else if (answerType === 'date') {
            updateData.date_answer = value || null;
          }

          await updateIRSFormAnswer(formId, questionId, updateData);
          // Refetch to get updated data including other_line_items
          refetch();
        } catch (error) {
          console.error('Failed to save answer:', error);
        } finally {
          setSaving((prev) => ({ ...prev, [questionId]: false }));
        }
      },
      500
    );

    return () => {
      // Cleanup on unmount
    };
  }, [formId, refetch]);

  const handleAnswerChange = (
    questionId: string,
    value: any,
    answerType: string
  ) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
    if (editable && debouncedSaveRef.current) {
      debouncedSaveRef.current(questionId, value, answerType);
    }
  };

  const handleOtherItemsUpdate = () => {
    refetch();
  };

  // Group questions by parent/child relationships
  const getQuestionGroups = () => {
    const parentQuestions: IRSQuestion[] = [];
    const childQuestionsMap: Record<string, IRSQuestion[]> = {};

    questions.forEach((q) => {
      if (!q.parent_question_id) {
        parentQuestions.push(q);
      } else {
        if (!childQuestionsMap[q.parent_question_id]) {
          childQuestionsMap[q.parent_question_id] = [];
        }
        childQuestionsMap[q.parent_question_id].push(q);
      }
    });

    // Sort parent questions by display_order
    parentQuestions.sort((a, b) => {
      const orderA = a.display_order || 0;
      const orderB = b.display_order || 0;
      return orderA - orderB;
    });

    // Sort child questions by display_order
    Object.keys(childQuestionsMap).forEach((parentId) => {
      childQuestionsMap[parentId].sort((a, b) => {
        const orderA = a.display_order || 0;
        const orderB = b.display_order || 0;
        return orderA - orderB;
      });
    });

    return { parentQuestions, childQuestionsMap };
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  const { parentQuestions, childQuestionsMap } = getQuestionGroups();

  return (
    <Grid container spacing={2}>
      {parentQuestions.map((question) => {
        const childQuestions = childQuestionsMap[question.question_id] || [];
        const parentAnswer = answers[question.question_id];

        return (
          <Grid item xs={12} key={question.question_id}>
            <IRSFormQuestion
              question={question}
              answer={answers[question.question_id]}
              onChange={(value) =>
                handleAnswerChange(
                  question.question_id,
                  value,
                  question.answer_type
                )
              }
              disabled={!editable || saving[question.question_id]}
              parentAnswer={parentAnswer}
              formId={formId}
              onOtherItemsUpdate={handleOtherItemsUpdate}
            />
            {/* Render child questions */}
            {childQuestions.map((childQuestion) => (
              <Box key={childQuestion.question_id} sx={{ ml: 4, mt: 1 }}>
                <IRSFormQuestion
                  question={childQuestion}
                  answer={answers[childQuestion.question_id]}
                  onChange={(value) =>
                    handleAnswerChange(
                      childQuestion.question_id,
                      value,
                      childQuestion.answer_type
                    )
                  }
                  disabled={!editable || saving[childQuestion.question_id]}
                  parentAnswer={parentAnswer}
                  formId={formId}
                  onOtherItemsUpdate={handleOtherItemsUpdate}
                />
              </Box>
            ))}
          </Grid>
        );
      })}
    </Grid>
  );
};

export default IRSFormSection;
